import type { ButtonHTMLAttributes } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  fullWidth?: boolean
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-cobalt text-white hover:bg-cobalt-deep active:bg-cobalt-deep disabled:bg-cobalt/50',
  secondary:
    'bg-ground-2 text-ink border border-line hover:bg-ground-3 active:bg-ground-3 disabled:text-ink-faint',
  ghost:
    'bg-transparent text-ink-muted hover:text-ink hover:bg-ground-3 active:bg-ground-3 disabled:text-ink-faint',
  danger:
    'bg-transparent text-terracotta-deep border border-terracotta-deep/40 hover:bg-terracotta-deep/10 active:bg-terracotta-deep/10 disabled:text-ink-faint',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'min-h-[44px] px-4 text-small',
  md: 'min-h-[44px] px-5 text-small font-medium',
  lg: 'min-h-[48px] px-7 text-body font-medium',
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 rounded-full transition-colors duration-micro ease-porcelain disabled:cursor-not-allowed ${variantClasses[variant]} ${sizeClasses[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && (
        <svg
          className="motion-safe:animate-spin"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )}
      {children}
    </button>
  )
}
